import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, getRouteApi, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { liveSportsMenuQuery } from '@/api/queries';
import type { LiveSortMode } from '@/domain/live-sort';
import { resolveSport } from '@/shell/sports-menu-utils';
import { LiveInlineSearch, LiveSortSwitch, LiveTournamentFilter } from '@/ui';
import { EventsFeedSkeleton } from './-components/EventsFeed';
import { LiveEventsFeed } from './-components/LiveEventsFeed';

const liveRouteApi = getRouteApi('/live');

export const Route = createFileRoute('/live/')({
  component: LiveIndexPage,
});

function LiveIndexPage() {
  const { sport: sportSlug, tournament: tournamentParam } = liveRouteApi.useSearch();
  const navigate = useNavigate({ from: '/live/' });
  const { t } = useTranslation();
  const { data: sports, isPending: sportsPending } = useQuery(liveSportsMenuQuery());
  const [selectedTournamentIds, setSelectedTournamentIds] = useState<number[]>([]);
  const [sortBy, setSortBy] = useState<LiveSortMode>('Competition');
  const [searchQuery, setSearchQuery] = useState('');

  const sport = useMemo(
    () => (sportSlug ? resolveSport(sports, sportSlug) : null),
    [sports, sportSlug],
  );

  useEffect(() => {
    setSelectedTournamentIds([]);
    setSearchQuery('');
  }, [sport?.Name]);

  if (sportsPending || !sport) {
    return <EventsFeedSkeleton />;
  }

  const hasSearch = searchQuery.trim().length > 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex min-w-0 items-center gap-2">
        <LiveSortSwitch value={sortBy} onChange={setSortBy} />
        <LiveInlineSearch value={searchQuery} onChange={setSearchQuery} />
      </div>

      <LiveTournamentFilter
        sportName={sport.Name}
        selectedTournamentIds={selectedTournamentIds}
        onChange={setSelectedTournamentIds}
      />

      <LiveEventsFeed
        sportName={sport.Name}
        selectedTournamentIds={selectedTournamentIds}
        sortBy={sortBy}
        searchQuery={searchQuery}
        emptyDescription={
          hasSearch
            ? t('NoSearchResults', {
                defaultValue: 'No events match your search.',
              })
            : t('NoLiveEventsForSport', {
                defaultValue: 'No live events for this sport right now.',
              })
        }
        onOpenEvent={({ eventId, media }) => {
          void navigate({
            to: '/live/$eventId',
            params: { eventId: String(eventId) },
            search: {
              sport: sportSlug,
              tournament: tournamentParam,
              media: media === 'stream' ? 'stream' : undefined,
            },
          });
        }}
      />
    </div>
  );
}
